/**
 * Modern Cart Drawer Component
 * 2025 Design - Mobile First, Accessible, Fast
 */

(() => {

class CartDrawerModern extends HTMLElement {
  constructor() {
    super();

    this.drawer = this;
    this.backdrop = this.querySelector('[data-drawer-overlay]');
    this.closeButton = this.querySelector('[data-drawer-close]');
    this.panel = this.querySelector('.cart-drawer__blocks');
    this.mainContent = this.querySelector('[data-drawer-content]');
    this.footer = this.querySelector('[data-cart-footer]');

    this.isOpen = false;
    this.isOpening = false;
    this.isLoading = false;
    this.stateToken = 0;
    this.openRaf = null;
    this.closeTimer = null;
    this.closeTransitionHandler = null;
    this.previouslyFocusedElement = null;

    // Get theme settings
    this.settings = window.theme?.settings || {};

    this.bindEvents();
    this.initializeCrossSell(); // Not awaiting here since constructor can't be async
  }

  refreshElements() {
    // Re-cache elements - useful after DOM changes
    this.backdrop = this.querySelector('[data-drawer-overlay]');
    this.closeButton = this.querySelector('[data-drawer-close]');
    this.panel = this.querySelector('.cart-drawer__blocks');
    this.mainContent = this.querySelector('[data-drawer-content]');
    this.footer = this.querySelector('[data-cart-footer]');
  }

  bindEvents() {
    // Refresh elements before binding
    this.refreshElements();

    // Close events
    this.closeButton?.addEventListener('click', () => this.close());
    this.backdrop?.addEventListener('click', () => this.close());

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Cart item events
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));

    // Discount form
    const discountForm = this.querySelector('[data-discount-form]');
    discountForm?.addEventListener('submit', this.handleDiscountSubmit.bind(this));

    // Checkout button
    const checkoutBtn = this.querySelector('[data-checkout]');
    checkoutBtn?.addEventListener('click', this.handleCheckout.bind(this));

    // Agreement checkbox
    const agreementCheckbox = this.querySelector('[data-agreement-checkbox]');
    agreementCheckbox?.addEventListener('change', this.handleAgreementChange.bind(this));

    // Listen for cart updates
    document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
    document.addEventListener('cart:item-added', () => {
      this.openOrRefresh();
    });
  }

  async handleClick(event) {
    // Use event.target for initial check, then closest for parent element
    // This ensures we catch clicks on SVG children as well
    const target = event.target;

    // Quantity buttons
    const decreaseBtn = target.closest('[data-quantity-decrease]');
    const increaseBtn = target.closest('[data-quantity-increase]');
    const removeBtn = target.closest('[data-remove-item]');
    const addCrossSell = target.closest('[data-add-to-cart]');
    const removeDiscountBtn = target.closest('[data-remove-discount]');

    if (decreaseBtn || increaseBtn) {
      event.preventDefault();
      event.stopPropagation();

      const quantityContainer = event.target.closest('.cart-item__quantity');
      const input = quantityContainer.querySelector('[data-quantity-input]');
      const display = quantityContainer.querySelector('.quantity__display');
      const currentQty = parseInt(input.value) || 1;
      const newQty = decreaseBtn ? Math.max(0, currentQty - 1) : currentQty + 1;

      // Check if button is already loading
      const item = event.target.closest('[data-cart-item]');
      if (item.classList.contains('is-loading')) {
        return; // Prevent multiple clicks while loading
      }

      // Check for max quantity limit (if previously set from stock error)
      const maxQty = item.dataset.maxQuantity;
      if (maxQty && newQty > parseInt(maxQty)) {
        this.showError(`You can only order maximum ${maxQty} of this product.`);
        // Revert to max quantity
        if (input) input.value = maxQty;
        if (display) display.textContent = maxQty;
        return;
      }

      // Update display immediately for better UX
      display.textContent = newQty;
      input.value = newQty;

      // Cancel any pending updates
      clearTimeout(this.updateTimeout);

      // Add debounce to prevent rapid clicks
      this.updateTimeout = setTimeout(() => {
        this.updateQuantity(input);
      }, 300);
    }

    if (removeBtn) {
      const item = removeBtn.closest('[data-cart-item]');
      const key = item.dataset.key;
      this.removeItem(key, item);
    }

    if (addCrossSell) {
      event.preventDefault();
      const button = addCrossSell;
      const productId = button.dataset.productId;
      const variantId = button.dataset.variantId;

      // Add loading state
      button.classList.add('is-loading');
      button.disabled = true;

      // Manual spinner addition - bypass CSS issues
      this.addSpinnerToButton(button);

      try {
        // Fetch product data to check variants (productId is actually the handle)
        const response = await fetch(`/products/${productId}.js`);
        if (!response.ok) throw new Error('Product not found');
        const product = await response.json();

        // Check if product has multiple variants or single variant with options
        const hasMultipleVariants = product.variants && product.variants.length > 1;
        const hasSizeOptions =
          product.options &&
          product.options.some((opt) => {
            const optName = (typeof opt === 'string' ? opt : opt.name || '').toLowerCase();
            return optName.includes('size') || optName.includes('beden');
          });

        if (hasMultipleVariants || hasSizeOptions) {
          // Product has variants - open product drawer for selection

          // Remove loading state before opening product drawer
          button.classList.remove('is-loading');
          button.disabled = false;
          this.removeSpinnerFromButton(button);

          // Close cart drawer first
          this.close();

          // Small delay to allow cart drawer to close smoothly
          setTimeout(() => {
            // Trigger product drawer
            const productDrawer = document.querySelector('quick-cart-drawer');
            if (productDrawer && typeof productDrawer.open === 'function') {
              productDrawer.open(product.handle, variantId);
            } else {
              window.location.href = `/products/${product.handle}`;
            }
          }, 300);
        } else {
          // Single variant - add directly
          await this.addToCart(variantId, 1, button);

          // Remove loading state from cross-sell button
          button.classList.remove('is-loading');
          button.disabled = false;
          this.removeSpinnerFromButton(button);
        }
      } catch (error) {
        console.error('Cross-sell add failed:', error);
        button.classList.remove('is-loading');
        button.disabled = false;
        this.removeSpinnerFromButton(button);
      }
    }

    // Remove discount tag
    if (removeDiscountBtn) {
      event.preventDefault();
      event.stopPropagation();

      const discountCode = removeDiscountBtn.dataset.removeDiscount;

      await this.removeDiscount(discountCode);
    }
  }

  handleChange(event) {
    const quantityInput = event.target.closest('[data-quantity-input]');
    const giftWrapCheckbox = event.target.closest('[data-gift-wrap]');
    const cartNoteTextarea = event.target.closest('[data-cart-note]');

    if (quantityInput) {
      this.updateQuantity(quantityInput);
    }

    if (giftWrapCheckbox) {
      this.toggleGiftWrap(giftWrapCheckbox);
    }

    if (cartNoteTextarea) {
      this.updateCartNote(cartNoteTextarea.value);
    }
  }

  async updateQuantity(input) {
    const item = input.closest('[data-cart-item]');
    if (!item) {
      console.error('Cart item element not found');
      this.showError('Item not found. Please refresh the page.');
      return;
    }

    const key = item.dataset.key;
    if (!key) {
      console.error('Cart item key not found', item);
      this.showError('Unable to identify item. Please refresh the page.');
      return;
    }

    const quantity = parseInt(input.value) || 0;
    // console.log('Updating quantity:', { key, quantity });

    // Show loading on item
    item.classList.add('is-loading');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: key, quantity }),
      });

      if (!response.ok) {
        // Handle stock limit errors (422) FIRST
        if (response.status === 422) {
          const errorData = await response.json().catch(() => null);

          // Parse the stock limit from the message (both English and Turkish formats)
          const matchEnglish = errorData?.message?.match(/Only (\d+) item/);
          const matchGeneric = errorData?.message?.match(/(\d+)/);
          const stockQty = matchEnglish ? parseInt(matchEnglish[1]) : matchGeneric ? parseInt(matchGeneric[1]) : null;


          // Store max quantity for this item to prevent future attempts
          if (stockQty !== null) {
            item.dataset.maxQuantity = stockQty;
            this.showError(`You can only order maximum ${stockQty} of this product.`);

            // Revert the display to max quantity
            const display = item.querySelector('.quantity__display');
            const quantityInput = item.querySelector('[data-quantity-input]');
            if (display) display.textContent = stockQty;
            if (quantityInput) quantityInput.value = stockQty;
          } else {
            // No specific quantity found, show general stock error
            this.showError('Unable to update quantity due to stock limit.');
          }

          // Remove loading state
          item.classList.remove('is-loading');
          return; // Exit early, don't throw error
        }

        // Other errors
        const errorData = await response.json().catch(() => null);
        console.error('Cart update failed:', response.status, errorData);
        throw new Error(errorData?.message || 'Failed to update quantity. Please try again.');
      }

      const cart = await response.json();

      if (quantity === 0) {
        // Animate removal
        item.classList.add('cart-item--removing');
        setTimeout(() => {
          // Render cart immediately with fresh data from response
          this.renderCart(cart);
        }, 300);
      } else {
        // Render cart immediately with fresh data from response
        this.renderCart(cart);
      }
    } catch (error) {
      // console.error('Quantity update failed:', error);
      this.showError('Failed to update quantity. Please try again.');

      // Try to revert quantity by fetching current cart
      try {
        const currentCartResponse = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        });
        if (currentCartResponse.ok) {
          const currentCart = await currentCartResponse.json();
          const originalItem = currentCart.items.find((i) => i.key === key);
          if (originalItem) {
            input.value = originalItem.quantity;
            const display = item.querySelector('.quantity__display');
            if (display) display.textContent = originalItem.quantity;
          }
        }
      } catch (revertError) {
        console.error('Failed to revert quantity:', revertError);
      }
    } finally {
      item.classList.remove('is-loading');
    }
  }

  async removeItem(key, itemElement) {
    itemElement.classList.add('cart-item--removing');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });

      if (!response.ok) throw new Error('Failed to remove item');

      const cart = await response.json();

      // Wait for animation then render with fresh data
      setTimeout(() => {
        // Render cart immediately with fresh data from response
        this.renderCart(cart);
      }, 300);
    } catch (error) {
      console.error('Remove item failed:', error);
      itemElement.classList.remove('cart-item--removing');
      this.showError('Failed to remove item. Please try again.');
    }
  }

  async addToCart(variantId, quantity = 1, buttonElement = null) {
    // Use provided button or try to find it
    const button = buttonElement || this.querySelector(`[data-variant-id="${variantId}"]`);

    try {
      // Add item to cart
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
        }),
      });

      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(errorData.description || 'Failed to add item');
      }

      // Get fresh cart data immediately (NO CACHE)
      const cartResponse = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      if (!cartResponse.ok) throw new Error('Failed to get cart');

      const cart = await cartResponse.json();

      // Render cart immediately with fresh data
      this.refreshElements();
      this.renderCart(cart);
    } catch (error) {
      console.error('Add to cart failed:', error);
      this.showError(error.message || 'Failed to add item. Please try again.');
      throw error; // Re-throw for caller to handle
    }
  }

  async handleDiscountSubmit(event) {
    event.preventDefault();

    const form = event.target;
    const input = form.querySelector('input[name="discount"]');
    const message = form.querySelector('[data-discount-message]');
    const discountCode = input.value.trim();

    if (!discountCode) return;

    // Show loading
    const submitBtn = form.querySelector('button[type="submit"]');
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Applying...';

    try {
      // Apply discount by fetching the discount URL
      const discountResponse = await fetch(`/discount/${encodeURIComponent(discountCode)}`);

      if (!discountResponse.ok) {
        throw new Error('Invalid discount code');
      }

      // Wait longer for Shopify to fully process the discount
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Get FRESH cart data with NO CACHE
      const cartResponse = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      if (!cartResponse.ok) throw new Error('Failed to get cart');

      const cart = await cartResponse.json();

      // Render cart immediately with fresh data
      this.renderCart(cart);

      // Clear the input
      input.value = '';

      // Show success message temporarily
      message.textContent = 'Discount code applied!';
      message.classList.remove('error');
      message.classList.add('success');
      message.hidden = false;

      // Hide message after 2 seconds
      setTimeout(() => {
        message.hidden = true;
      }, 2000);
    } catch (error) {
      console.error('Discount application error:', error);
      message.textContent = 'Invalid discount code';
      message.classList.remove('success');
      message.classList.add('error');
      message.hidden = false;
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = originalText;
    }
  }

  /**
   * Update applied discount tags dynamically
   */
  updateAppliedDiscountTags(cart) {
    const tagsContainer = this.querySelector('[data-applied-discounts]');
    if (!tagsContainer) return;

    // Get applicable discount codes
    const applicableDiscounts = cart.discount_codes?.filter((code) => code.applicable) || [];

    if (applicableDiscounts.length === 0) {
      tagsContainer.innerHTML = '';
      return;
    }

    // Render discount tags
    tagsContainer.innerHTML = applicableDiscounts
      .map((discount) => {
        return `
          <div class="discount-tag" data-discount-tag="${this.escapeHtml(discount.code)}">
            <span class="discount-tag__code">${this.escapeHtml(discount.code.toUpperCase())}</span>
            <button
              type="button"
              class="discount-tag__remove"
              data-remove-discount="${this.escapeHtml(discount.code)}"
              aria-label="Remove ${this.escapeHtml(discount.code)}"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M8 2L2 8M2 2L8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        `;
      })
      .join('');
  }

  /**
   * Remove discount code using Shopify Cart API
   * Updates cart without page reload
   */
  async removeDiscount(discountCode) {
    try {
      // Show loading state
      const tagsContainer = this.querySelector('[data-applied-discounts]');
      if (tagsContainer) {
        tagsContainer.style.opacity = '0.5';
        tagsContainer.style.pointerEvents = 'none';
      }

      // Use Shopify's Cart API to clear discount
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          discount: '', // Empty string clears all discounts
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove discount');
      }

      // Fetch fresh cart data
      const cartResponse = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      const cart = await cartResponse.json();

      // Update cart UI without page reload
      this.renderCart(cart);

      // Show success message
      const message = this.querySelector('[data-discount-message]');
      if (message) {
        message.textContent = 'Discount code removed';
        message.classList.remove('error');
        message.classList.add('success');
        message.hidden = false;

        setTimeout(() => {
          message.hidden = true;
        }, 2000);
      }
    } catch (error) {
      console.error('[Cart Drawer] Failed to remove discount:', error);

      // Reset loading state
      const tagsContainer = this.querySelector('[data-applied-discounts]');
      if (tagsContainer) {
        tagsContainer.style.opacity = '1';
        tagsContainer.style.pointerEvents = 'auto';
      }

      this.showError('Failed to remove discount. Please try again.');
    }
  }

  escapeHtml(unsafe) {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'GBP';

    // Simple formatting - you can enhance this based on your needs
    const symbols = {
      GBP: '£',
      USD: '$',
      EUR: '€',
    };

    const symbol = symbols[currency] || currency;
    return `${symbol}${amount}`;
  }

  async updateCartNote(note) {
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ note }),
      });
    } catch (error) {
      console.error('Failed to update cart note:', error);
    }
  }

  async toggleGiftWrap(checkbox) {
    const isChecked = checkbox.checked;
    const price = parseInt(checkbox.dataset.price) || 0;

    // This would require a gift wrap product/variant to be set up
    // For now, just visual feedback
    if (isChecked) {
      checkbox.closest('.cart-item').classList.add('has-gift-wrap');
    } else {
      checkbox.closest('.cart-item').classList.remove('has-gift-wrap');
    }
  }

  handleAgreementChange(event) {
    const checkbox = event.target;
    const checkoutBtn = this.querySelector('[data-checkout]');

    if (this.settings.cart_agreement_required) {
      checkoutBtn.disabled = !checkbox.checked;
    }
  }

  async handleCheckout() {
    // Check agreement if required
    if (this.settings.cart_show_agreement_checkbox && this.settings.cart_agreement_required) {
      const agreementCheckbox = this.querySelector('[data-agreement-checkbox]');
      if (!agreementCheckbox?.checked) {
        // Highlight the checkbox
        const agreementContainer = this.querySelector('.cart-drawer__agreement');
        agreementContainer?.classList.add('error-shake');
        setTimeout(() => {
          agreementContainer?.classList.remove('error-shake');
        }, 500);
        return;
      }
    }

    // Track checkout event
    if (window.analytics) {
      window.analytics.track('Checkout Started', {
        cart_total: this.getCartTotal(),
        item_count: this.getItemCount(),
      });
    }

    window.location.href = '/checkout';
  }

  async refresh() {
    this.showLoading();

    try {
      // NO CACHE - Always get fresh data
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch cart');

      const cart = await response.json();
      this.renderCart(cart);

      // Update cross-sell visibility and content based on free shipping threshold
      if (this.settings.cart_show_cross_sell) {
        await this.loadCrossSellProducts(); // Ensure products are loaded
        this.renderCrossSell(cart);
      }
    } catch (error) {
      console.error('Cart refresh failed:', error);
      this.showError('Failed to load cart. Please refresh the page.');
    } finally {
      this.hideLoading();
    }
  }

  renderCart(cart) {
    // Re-query footer element in case DOM has changed
    if (!this.footer) {
      this.footer = this.querySelector('[data-cart-footer]');
    }

    // Update count
    const countElement = this.querySelector('[data-cart-count]');
    if (countElement) {
      countElement.textContent = cart.item_count;
    }

    window.SEMUHeader?.renderCartCount?.(cart.item_count, { animate: true });

    // Update free shipping progress (do this before empty state check)
    this.updateShippingProgress(cart);

    // Update cross-sell visibility and content (do this before empty state check)
    if (this.settings.cart_show_cross_sell) {
      this.renderCrossSell(cart);
    }

    // Empty state
    if (cart.item_count === 0) {
      this.classList.add('is-empty');
      this.mainContent.innerHTML = this.getEmptyTemplate();
      if (this.footer) {
        this.footer.hidden = true;
      }
      return;
    }

    this.classList.remove('is-empty');
    if (this.footer) {
      this.footer.hidden = false;
      this.footer.style.display = 'flex'; // Force display
      this.footer.removeAttribute('hidden'); // Ensure hidden attribute is removed
    }

    // Render items
    const itemsContainer = this.querySelector('[data-cart-items]') || this.mainContent;
    itemsContainer.innerHTML = cart.items.map((item) => this.getItemTemplate(item)).join('');

    // Update totals
    this.updateTotals(cart);

    // Update applied discount tags
    this.updateAppliedDiscountTags(cart);

    // Final check to ensure footer is visible
    if (!this.footer) {
      this.footer = this.querySelector('[data-cart-footer]');
    }
    if (this.footer && cart.item_count > 0) {
      this.footer.hidden = false;
      this.footer.removeAttribute('hidden');
      this.footer.style.display = 'flex';
    }
  }

  renderCrossSell(cart) {
    // console.log('[DEBUG] renderCrossSell called with cart:', cart);

    // Cross-sell can work not only with JavaScript but also with Liquid template
    // First check the cross-sell section in Liquid template
    const crossSellSection = this.querySelector('[data-cross-sell]');
    // console.log('[DEBUG] Cross-sell section found:', !!crossSellSection);

    if (!crossSellSection) {
      // console.log('[DEBUG] No cross-sell section found in DOM');
      return;
    }

    const threshold = (parseFloat(this.settings.cart_free_shipping_threshold) || 100) * 100;
    const remaining = threshold - cart.total_price;

    // console.log('[DEBUG] Free shipping threshold:', threshold);
    // console.log('[DEBUG] Cart total:', cart.total_price);
    // console.log('[DEBUG] Remaining for free shipping:', remaining);

    // ALWAYS show cross-sell section, don't hide it
    // The Liquid template already handles showing different messages
    if (cart.item_count === 0) {
      crossSellSection.style.display = 'none';
      // console.log('[DEBUG] Hiding cross-sell: cart is empty');
      return;
    }

    // Always show cross-sell section
    crossSellSection.style.display = 'block';
    // console.log('[DEBUG] Cross-sell section displayed');

    // Update title with remaining amount (only update title, don't touch products)
    const crossSellTitle = crossSellSection.querySelector('.cross-sell__title');
    const crossSellSubtitle = crossSellSection.querySelector('.cross-sell__subtitle');
    // console.log('[DEBUG] Cross-sell title element found:', !!crossSellTitle);
    if (crossSellTitle) {
      if (remaining > 0) {
        // Still need more for free shipping
        crossSellTitle.textContent = `${this.formatMoney(remaining)} away from free shipping`;
        if (crossSellSubtitle) {
          crossSellSubtitle.textContent = 'Complete your order to get free shipping';
        }
      } else {
        // Free shipping threshold reached
        crossSellTitle.textContent = 'You might also like';
        if (crossSellSubtitle) {
          crossSellSubtitle.textContent = 'Complete your look with these items';
        }
      }
    }

    // If products are already rendered in Liquid template, only show those not in cart
    const existingCards = crossSellSection.querySelectorAll('.cross-sell__card');
    const cartProductIds = cart.items.map((item) => Number(item.product_id));

    existingCards.forEach((card) => {
      const button = card.querySelector('[data-product-id]');
      if (button) {
        const productId = Number(button.dataset.productId);
        if (cartProductIds.includes(productId)) {
          card.style.display = 'none'; // Hide products already in cart
        } else {
          card.style.display = 'block'; // Show products not in cart
        }
      }
    });
  }

  getTranslation(key, defaultValue) {
    // Try to get translation from theme object
    const keys = key.split('.');
    let translation = window.theme?.translations;

    for (const k of keys) {
      if (translation && typeof translation === 'object') {
        translation = translation[k];
      } else {
        return defaultValue;
      }
    }

    return translation || defaultValue;
  }

  getItemTemplate(item) {
    const hasImage = this.settings.cart_show_product_image !== false;

    // Optimize image URL - aggressive quality reduction for max speed
    let imageUrl = item.image;
    if (imageUrl) {
      if (imageUrl.includes('?')) {
        imageUrl = `${imageUrl}&width=120&quality=50`;
      } else {
        imageUrl = `${imageUrl}?width=120&quality=50`;
      }
    }

    return `
      <div class="cart-item" data-cart-item data-key="${item.key}" data-product-id="${item.product_id}">
        ${
          hasImage && item.image
            ? `
          <div class="cart-item__media">
            <img 
              src="${imageUrl}"
              alt="${this.escapeHtml(item.product_title)}"
              loading="lazy"
              width="78"
              height="118">
          </div>
        `
            : ''
        }
        
        <div class="cart-item__details">
          <p class="cart-item__name">${this.escapeHtml(item.product_title)}</p>
          
          ${
            item.variant_title && item.variant_title !== 'Default Title'
              ? `<div class="cart-item__variant">${item.variant_title.replace(/ \/ /g, '/')}</div>`
              : ''
          }
          
          ${
            this.settings.cart_show_sku && item.sku
              ? `<div class="cart-item__sku">SKU: ${this.escapeHtml(item.sku)}</div>`
              : ''
          }
          
          <div class="cart-item__price">
            ${this.formatMoney(item.final_line_price)}
          </div>
          
          <div class="cart-item__controls">
            <div class="cart-item__quantity">
              <button type="button" class="quantity__button" data-quantity-decrease>−</button>
              <span class="quantity__display">${item.quantity}</span>
              <button type="button" class="quantity__button" data-quantity-increase>+</button>
              <input type="hidden" class="quantity__input" value="${item.quantity}" data-quantity-input>
            </div>
          </div>
        </div>

        <button type="button" class="cart-item__remove" data-remove-item aria-label="Remove">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 4h12M5 4V2.5A.5.5 0 015.5 2h5a.5.5 0 01.5.5V4m2 0v9.5a.5.5 0 01-.5.5h-9a.5.5 0 01-.5-.5V4h10zM6.5 7v4M9.5 7v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;
  }

  getEmptyTemplate() {
    return `
      <div class="cart-drawer__empty">
        <div class="empty-state">
          <svg class="empty-state__icon" xmlns="http://www.w3.org/2000/svg" width="80" height="80" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
          </svg>
          <p class="empty-state__title">Your cart is empty</p>
          <p class="empty-state__text">Add items to start shopping</p>
          <a href="/collections/all" class="button button--primary">
            Continue Shopping
          </a>
        </div>
      </div>
    `;
  }

  updateTotals(cart) {
    // Subtotal (use original_total_price if available, otherwise total_price)
    const subtotalElement = this.querySelector('[data-cart-subtotal]');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(cart.original_total_price || cart.total_price);
    }

    // Update discount display
    this.updateDiscounts(cart);

    // Update savings
    this.updateSavings(cart);

    // Total
    const totalElement = this.querySelector('[data-cart-total]');
    if (totalElement) {
      totalElement.textContent = this.formatMoney(cart.total_price);
    }
  }

  updateDiscounts(cart) {
    const discountsContainer = this.querySelector('[data-cart-discounts]');
    if (!discountsContainer) return;

    // Check if we have any discounts
    // Shopify discount codes are in discount_codes array, NOT cart_level_discount_applications
    const hasDiscountCodes = cart.discount_codes && cart.discount_codes.some((d) => d.applicable);
    const hasTotalDiscount = cart.total_discount && cart.total_discount > 0;

    if (!hasDiscountCodes && !hasTotalDiscount) {
      discountsContainer.style.display = 'none';
      discountsContainer.innerHTML = '';
      return;
    }

    let discountsHTML = '';

    // Discount codes (session-based discounts)
    if (hasDiscountCodes) {
      const applicableDiscounts = cart.discount_codes.filter((d) => d.applicable);
      discountsHTML = applicableDiscounts
        .map((discount) => {
          const percentage =
            cart.original_total_price && cart.total_discount
              ? Math.round((cart.total_discount / cart.original_total_price) * 100)
              : 0;
          return `
            <div class="cart-drawer__discount-item" data-discount-code="${this.escapeHtml(discount.code)}">
              <span class="discount__label">
                <span class="discount-code-name">${this.escapeHtml(discount.code.toUpperCase())}</span>
                <span class="discount__percentage">(-${percentage}%)</span>
              </span>
              <span class="discount__amount">-${this.formatMoney(cart.total_discount)}</span>
            </div>
          `;
        })
        .join('');
    } else if (hasTotalDiscount) {
      // Item-level discounts (automatic/product discounts)
      const percentage = cart.original_total_price
        ? Math.round((cart.total_discount / cart.original_total_price) * 100)
        : 0;
      discountsHTML = `
        <div class="cart-drawer__discount-item">
          <span class="discount__label">
            <span class="discount-code-name">Product Discount</span>
            <span class="discount__percentage">(-${percentage}%)</span>
          </span>
          <span class="discount__amount">-${this.formatMoney(cart.total_discount)}</span>
        </div>
      `;
    }

    discountsContainer.innerHTML = discountsHTML;
    discountsContainer.style.display = 'flex';
  }

  updateSavings(cart) {
    const savingsEl = this.querySelector('[data-cart-savings]');
    if (!savingsEl) return;

    // Hide savings message - not needed
    savingsEl.style.display = 'none';
  }

  updateShippingProgress(cart) {
    // Disabled: Free shipping message removed from header
    // Only shown in cross-sell section
    return;
  }

  async initializeCrossSell() {
    // console.log('[DEBUG] initializeCrossSell called');
    // console.log('[DEBUG] settings:', this.settings);
    // console.log('[DEBUG] cart_show_cross_sell:', this.settings.cart_show_cross_sell);
    // console.log('[DEBUG] cart_drawer_collection:', this.settings.cart_drawer_collection);

    if (!this.settings.cart_show_cross_sell) {
      // console.log('[DEBUG] Cross-sell is disabled in settings');
      return;
    }

    // Get collection from settings or data attribute with fallback to 'all'
    this.crossSellCollection =
      this.settings.cart_drawer_collection || this.getAttribute('data-cross-sell-collection') || 'all';
    // console.log('[DEBUG] crossSellCollection set to:', this.crossSellCollection);

    if (!this.crossSellCollection || this.crossSellCollection === '') {
      // console.log('[DEBUG] No collection configured for cross-sell, using default: all');
      this.crossSellCollection = 'all';
    }

    await this.loadCrossSellProducts();
  }

  async loadCrossSellProducts() {
    if (!this.crossSellCollection) {
      return;
    }

    // Force reload if we need fresh data
    const forceReload = this.getAttribute('data-force-reload-cross-sell') === 'true';

    // Only load once if already loaded (unless force reload)
    if (this.crossSellProducts !== undefined && !forceReload) {
      return;
    }

    // Reset force reload flag
    if (forceReload) {
      this.removeAttribute('data-force-reload-cross-sell');
    }

    try {
      const response = await fetch(`/collections/${this.crossSellCollection}/products.json?limit=10`, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        console.error('[Cross-Sell] Failed response:', response.status, response.statusText);

        // If collection not found, try 'all' as fallback
        if (response.status === 404 && this.crossSellCollection !== 'all') {
          this.crossSellCollection = 'all';
          return await this.loadCrossSellProducts(); // Recursive call with 'all'
        }

        throw new Error(`Failed to load cross-sell products: ${response.status}`);
      }

      const data = await response.json();
      this.crossSellProducts = data.products || [];
    } catch (error) {
      console.error('[Cross-Sell] Error loading products:', error);
      this.crossSellProducts = [];
    }
  }

  waitForStylesheet() {
    const stylesheet = document.getElementById('CartDrawerStyles');

    if (!stylesheet) {
      return Promise.resolve(window.getComputedStyle(this).position === 'fixed');
    }

    if (stylesheet.dataset.loadState === 'loaded' || (stylesheet.sheet && stylesheet.media === 'all')) {
      return Promise.resolve(true);
    }

    if (stylesheet.dataset.loadState === 'error') {
      return Promise.resolve(false);
    }

    return new Promise((resolve) => {
      let settled = false;
      let timeoutId = null;

      const cleanup = () => {
        stylesheet.removeEventListener('load', onLoad);
        stylesheet.removeEventListener('error', onError);
        if (timeoutId) clearTimeout(timeoutId);
      };

      const finish = (ready) => {
        if (settled) return;
        settled = true;
        cleanup();

        if (!ready) {
          resolve(false);
          return;
        }

        requestAnimationFrame(() => resolve(true));
      };

      const onLoad = () => finish(true);
      const onError = () => finish(false);

      stylesheet.addEventListener('load', onLoad, { once: true });
      stylesheet.addEventListener('error', onError, { once: true });
      timeoutId = setTimeout(() => finish(false), 5000);

      // The load event may have fired between the initial check and listener setup.
      if (stylesheet.dataset.loadState === 'loaded' || (stylesheet.sheet && stylesheet.media === 'all')) {
        finish(true);
      } else if (stylesheet.dataset.loadState === 'error') {
        finish(false);
      }
    });
  }

  getTransitionDuration(element) {
    if (!element) return 0;

    const styles = window.getComputedStyle(element);
    const parseTimeList = (value) =>
      value.split(',').map((time) => {
        const normalized = time.trim();
        const parsed = Number.parseFloat(normalized) || 0;
        return normalized.endsWith('ms') ? parsed : parsed * 1000;
      });

    const durations = parseTimeList(styles.transitionDuration);
    const delays = parseTimeList(styles.transitionDelay);
    const itemCount = Math.max(durations.length, delays.length);
    let maximum = 0;

    for (let index = 0; index < itemCount; index += 1) {
      const duration = durations[index % durations.length] || 0;
      const delay = delays[index % delays.length] || 0;
      maximum = Math.max(maximum, duration + delay);
    }

    return maximum;
  }

  clearCloseWait() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (this.panel && this.closeTransitionHandler) {
      this.panel.removeEventListener('transitionend', this.closeTransitionHandler);
      this.closeTransitionHandler = null;
    }
  }

  hideAfterClose(token) {
    this.clearCloseWait();

    const finish = () => {
      if (token !== this.stateToken || this.isOpen) return;
      this.clearCloseWait();
      this.hidden = true;
    };

    const duration = this.getTransitionDuration(this.panel);
    if (!this.panel || duration === 0) {
      finish();
      return;
    }

    this.closeTransitionHandler = (event) => {
      if (event.target === this.panel && event.propertyName === 'transform') {
        finish();
      }
    };

    this.panel.addEventListener('transitionend', this.closeTransitionHandler);
    this.closeTimer = setTimeout(finish, duration + 100);
  }

  redirectToCart() {
    const cartUrl = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
    window.location.assign(cartUrl);
  }

  async open() {
    if (this.isOpen || this.isOpening) return false;

    this.isOpening = true;
    const token = ++this.stateToken;
    this.clearCloseWait();

    const stylesReady = await this.waitForStylesheet();
    if (token !== this.stateToken) return false;

    this.isOpening = false;
    if (!stylesReady) {
      this.redirectToCart();
      return false;
    }

    this.refreshElements();
    this.previouslyFocusedElement = document.activeElement;
    this.isOpen = true;
    this.hidden = false;
    this.inert = false;
    this.setAttribute('aria-hidden', 'false');

    // Commit the off-canvas closed geometry before starting its transition.
    void this.panel?.offsetWidth;
    this.openRaf = requestAnimationFrame(() => {
      this.openRaf = null;
      if (token !== this.stateToken || !this.isOpen) return;

      this.classList.add('is--open');
      document.body.style.overflow = 'hidden';
      this.trapFocus();
      this.dispatchEvent(new CustomEvent('cart-drawer:open'));

      this.refreshElements();
      this.refresh();
    });

    return true;
  }

  async openOrRefresh() {
    const opened = await this.open();
    if (!opened && this.isOpen) await this.refresh();
    return opened;
  }

  close() {
    if (!this.isOpen && !this.isOpening) return;

    const wasOpen = this.isOpen;
    const token = ++this.stateToken;
    this.isOpening = false;
    this.isOpen = false;

    if (this.openRaf) {
      cancelAnimationFrame(this.openRaf);
      this.openRaf = null;
    }

    if (!wasOpen) return;

    this.classList.remove('is--open');
    this.releaseFocus();

    if (
      this.previouslyFocusedElement instanceof HTMLElement &&
      this.previouslyFocusedElement.isConnected &&
      !this.contains(this.previouslyFocusedElement)
    ) {
      this.previouslyFocusedElement.focus({ preventScroll: true });
    }
    this.previouslyFocusedElement = null;

    this.inert = true;
    this.setAttribute('aria-hidden', 'true');

    if (!document.querySelector('quick-cart-drawer.is--open')) {
      document.body.style.overflow = '';
    }

    this.hideAfterClose(token);
    this.dispatchEvent(new CustomEvent('cart-drawer:close'));
  }

  trapFocus() {
    this.releaseFocus();

    const getFocusableElements = () =>
      Array.from(
        this.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => {
        const styles = window.getComputedStyle(element);
        return !element.hidden && !element.closest('[hidden], [inert]') && styles.visibility !== 'hidden' && element.getClientRects().length > 0;
      });

    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    this.addEventListener('keydown', this.focusTrapHandler);
    getFocusableElements()[0]?.focus({ preventScroll: true });
  }

  releaseFocus() {
    if (!this.focusTrapHandler) return;
    this.removeEventListener('keydown', this.focusTrapHandler);
    this.focusTrapHandler = null;
  }

  showLoading() {
    this.isLoading = true;
    this.classList.add('is-loading');
  }

  hideLoading() {
    this.isLoading = false;
    this.classList.remove('is-loading');
  }

  showError(message) {
    const errorTemplate = document.getElementById('cart-drawer-error');
    if (!errorTemplate) {
      console.error('[Cart Drawer] Error template not found!');
      return;
    }

    const error = errorTemplate.content.cloneNode(true);
    const messageElement = error.querySelector('[data-error-message]');
    if (messageElement) {
      messageElement.textContent = message;
    }

    this.mainContent.prepend(error);

    // Remove after 5 seconds
    setTimeout(() => {
      this.querySelector('.cart-drawer__error')?.remove();
    }, 5000);
  }

  formatMoney(cents) {
    const format = window.theme?.settings?.currency_display_format || 'symbol_only';

    // Shopify.formatMoney yoksa manuel format
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney && window.theme?.moneyFormat) {
      let formatted;

      switch (format) {
        case 'code_only':
          // Get formatted price and remove symbols
          const temp = Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
          const currency = window.theme?.currency || 'USD';
          // Remove common currency symbols like in price.liquid
          const noSymbol = temp.replace(/[$£€¥₺₹¢₽₩]/g, '').trim();
          formatted = `${noSymbol} ${currency}`;
          break;

        case 'symbol_and_code':
          formatted = Shopify.formatMoney(
            cents,
            window.theme?.moneyWithCurrencyFormat ||
              `${window.theme?.moneyFormat || '${{amount}}'} ${window.theme?.currency || 'USD'}`
          );
          break;

        case 'symbol_only':
        default:
          formatted = Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
          break;
      }

      return formatted;
    }

    // Fallback - manuel formatting
    const amount = (cents / 100).toFixed(2);
    const currency = window.theme?.currency || 'USD';
    const moneyFormat = window.theme?.moneyFormat || '${{amount}}';

    switch (format) {
      case 'code_only':
        return `${amount} ${currency}`;
      case 'symbol_and_code':
        // Try to extract symbol from moneyFormat
        const symbol = moneyFormat.match(/[£$€¥₺₹¢₽₩]/)?.[0] || '$';
        return `${symbol}${amount} ${currency}`;
      case 'symbol_only':
      default:
        // Use moneyFormat pattern
        if (moneyFormat.includes('{{amount}}')) {
          return moneyFormat.replace('{{amount}}', amount);
        } else {
          // Default to common format
          const symbol = moneyFormat.match(/[£$€¥₺₹¢₽₩]/)?.[0] || '$';
          return `${symbol}${amount}`;
        }
    }
  }

  escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };

    return text.replace(/[&<>"']/g, (m) => map[m]);
  }

  getCartTotal() {
    const totalElement = this.querySelector('[data-cart-total]');
    return totalElement ? totalElement.textContent : '0';
  }

  getItemCount() {
    const countElement = this.querySelector('[data-cart-count]');
    return countElement ? parseInt(countElement.textContent) || 0 : 0;
  }

  handleCartUpdate(event) {
    const { cart } = event.detail;
    if (cart) {
      this.renderCart(cart);
    }
  }

  // Manual spinner methods - to bypass CSS issues
  addSpinnerToButton(button) {
    // Use CSS spinner - more performant
    // Hide SVG (also hidden by CSS but for extra security)
    const svg = button.querySelector('svg');
    if (svg) svg.style.opacity = '0';
  }

  removeSpinnerFromButton(button) {
    // Show SVG again
    const svg = button.querySelector('svg');
    if (svg) svg.style.opacity = '1';
  }
}

// Register the element once. Shopify's Theme Editor can re-evaluate assets while
// replacing sections, so an unconditional definition can otherwise throw.
if (!customElements.get('cart-drawer-modern')) {
  customElements.define('cart-drawer-modern', CartDrawerModern);
}

function initializeCartDrawerDelegates() {
  if (window.__semuCartDrawerDelegatesBound) return;
  window.__semuCartDrawerDelegatesBound = true;

  // A delegated owner keeps newly rendered header links working and guarantees
  // that an anchor carrying both selectors is handled only once per click.
  document.addEventListener('click', (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const trigger = event.target.closest?.('[data-cart-drawer-trigger], a[href="/cart"]');
    if (!trigger) return;

    const drawer = document.querySelector('cart-drawer-modern');
    const shouldUseDrawer =
      trigger.hasAttribute('data-cart-drawer-trigger') ||
      window.theme?.settings?.cart_type === 'drawer';

    if (!drawer || !shouldUseDrawer) return;

    event.preventDefault();
    drawer.openOrRefresh();
  });

  document.addEventListener('cart:add', (event) => {
    const drawer = document.querySelector('cart-drawer-modern');
    if (!drawer || window.theme?.settings?.cart_type !== 'drawer') return;

    if (event.detail?.openDrawer !== false) {
      drawer.openOrRefresh();
    } else {
      drawer.refresh();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCartDrawerDelegates, { once: true });
} else {
  initializeCartDrawerDelegates();
}
})();
